import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { emitTodosChanged, useTodosChanged } from "../lib/todoSync";
import { toast } from "./ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { CalendarDays, ChevronDown, ChevronUp, CheckCircle2, Circle, AlertTriangle, Printer, LogOut, Flame, Lock } from "lucide-react";

const CALL_OUTCOMES = [
  { v: "已接通", en: "Connected" }, { v: "未接听", en: "No answer" }, { v: "留了语音", en: "Voicemail" },
  { v: "已约下次沟通", en: "Next call booked" }, { v: "暂无意向", en: "Not interested now" },
];

export default function WeekAgendaCalendar({ embedded = false }) {
  const { api, lang, user } = useApp();
  const nav = useNavigate();
  const zh = lang === "zh";
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState(null);
  const [clocking, setClocking] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const prevPending = useRef(null);
  const [deferItem, setDeferItem] = useState(null);
  const [deferReason, setDeferReason] = useState("");
  const [deferBusy, setDeferBusy] = useState(false);
  const [badges, setBadges] = useState([]);
  const [outcomeItem, setOutcomeItem] = useState(null);
  const [outcomeChoice, setOutcomeChoice] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [outcomeBusy, setOutcomeBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/me/agenda").then(({ data }) => setData(data)).catch(() => setData(null));
    api.get("/me/badges").then(({ data }) => setBadges(data.badges || [])).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);
  useTodosChanged(load, "agenda");   // 顶栏 / 我的待办页 / 聊天 写入 → 今日必做同步

  useEffect(() => {
    if (!data || data.isManager) return;
    const p = data.pending;
    const prev = prevPending.current;
    prevPending.current = p;
    if (prev != null && prev > 0 && p === 0) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 4200);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (!data || data.isManager) return null;

  const { today, todayItems = [], laterGroups = [], completedToday = [], pending = 0, canClockOut, clockedOut, gateEnabled } = data;
  const doneN = completedToday.length;
  const totalN = doneN + pending;
  const pct = totalN ? Math.round((doneN / totalN) * 100) : 100;
  const allDone = pending === 0 && totalN > 0;

  const complete = async (it) => {
    setBusy(it.id);
    try {
      if (it.kind === "todo") {
        await api.patch(`/todos/${it.id}`, { status: "done" });
      } else {
        const note = window.prompt(zh ? "填写处理说明（必填）" : "Enter a note (required)");
        if (!note || !note.trim()) { setBusy(null); return; }
        await api.post(`/crm/my-tasks/${it.id}/done`, { note: note.trim() });
      }
      toast.success(zh ? "已完成，收起到「今日已完成」" : "Completed");
      load(); emitTodosChanged("agenda");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed"));
      // 待办在工作台打开期间逾期时，同步最新状态，避免用户反复点击已被服务端拦截的完成按钮。
      if (it.kind === "todo" && e?.response?.status === 409) load();
    }
    setBusy(null);
  };

  const clockOut = async () => {
    setClocking(true);
    try {
      await api.post("/me/clock-out");
      toast.success(zh ? "打卡下班成功，辛苦了！" : "Clocked out. Great job!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "无法打卡下班" : "Cannot clock out")); }
    setClocking(false);
  };

  const submitDefer = async () => {
    if (!deferReason.trim()) { toast.error(zh ? "请填写延期理由" : "Reason required"); return; }
    setDeferBusy(true);
    try {
      await api.post(`/crm/tasks/${deferItem.id}/defer-request`, { reason: deferReason.trim() });
      toast.success(zh ? "延期申请已提交，等待经理审批" : "Defer requested");
      setDeferItem(null); setDeferReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "申请失败" : "Failed")); }
    setDeferBusy(false);
  };

  const submitOutcome = async () => {
    if (!outcomeChoice) { toast.error(zh ? "请选择回访结果" : "Pick an outcome"); return; }
    setOutcomeBusy(true);
    try {
      await api.post(`/todos/${outcomeItem.id}/call-outcome`, { outcome: outcomeChoice, note: outcomeNote.trim() });
      toast.success(zh ? "回访结果已记录并同步到客户时间线" : "Outcome logged & synced to lead");
      setOutcomeItem(null); setOutcomeChoice(""); setOutcomeNote(""); load(); emitTodosChanged("agenda");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "记录失败" : "Failed")); }
    setOutcomeBusy(false);
  };

  const prioDot = (p) => (p === "high" ? "bg-red-500" : p === "low" ? "bg-slate-300" : "bg-amber-400");
  // 界面、已完成列表与打印共用中性标题，避免权限变化时旧数据残留在页面或打印件里。
  const visibleTitle = (item) => item.projectAccessRestricted ? (zh ? "待办权限已变化，请联系主管" : "Task access changed. Contact your manager.") : item.title;

  const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const printWeek = () => {
    const secHtml = (title, its) => `<h3 style="color:#dc2626;margin:14px 0 4px;font-size:14px">${title}</h3><ul style="margin:0;padding-left:18px;font-size:12px;line-height:1.7;color:#334155">${its.length ? its.map((it) => `<li${it.overdue ? ' style="color:#dc2626;font-weight:600"' : ""}>${it.overdue ? "⚠ " : ""}${esc(visibleTitle(it))}${it.projectAccessRestricted ? "" : it.kind === "crm" ? (zh ? "（今日必做）" : " (must-do)") : it.kind === "appt" ? (it.apptKind === "rfq_visit" ? (zh ? "（分包踏勘）" : " (site visit)") : (zh ? "（现场评估）" : " (site eval)")) : ""}</li>`).join("") : '<li style="color:#bbb;list-style:none">—</li>'}</ul>`;
    let body = secHtml(zh ? "今日必做（含逾期）" : "Today (incl. overdue)", todayItems);
    laterGroups.forEach((g) => { if (g.items.length) body += secHtml(`${g.weekTag === "next" ? (zh ? "下周·" : "Next·") : ""}${g.label} ${g.date.slice(5)}`, g.items); });
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${zh ? "我的工作台" : "My Agenda"}</title></head>
      <body style="font-family:-apple-system,'PingFang SC','Microsoft YaHei',sans-serif;padding:24px;color:#0f172a;max-width:640px">
      <h2 style="color:#dc2626;margin:0 0 2px">${zh ? "我的工作清单" : "My Work Agenda"}</h2>
      <p style="margin:0 0 8px;color:#64748b;font-size:12px">${new Date().toLocaleString()}</p>
      ${body}</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error(zh ? "请允许浏览器弹出窗口" : "Please allow popups"); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 350);
  };

  const ItemRow = ({ it, dim }) => (
    <div className={`rounded-lg border p-2 text-xs ${it.kind === "appt" ? "border-sky-200 bg-sky-50/70" : it.overdue ? "border-red-200 bg-red-50" : dim ? "border-slate-100 bg-slate-50/40" : "border-slate-200 bg-white"}`} data-testid={`agenda-item-${it.id}`}>
      <div className="flex items-start gap-2">
        {it.projectAccessRestricted ? (
          <span data-testid={`agenda-restricted-${it.id}`} title={zh ? "请联系主管处理" : "Contact your supervisor"}
            className="mt-0.5 text-slate-400 shrink-0"><Lock className="h-4 w-4" /></span>
        ) : it.kind === "appt" ? (
          <CalendarDays className="h-4 w-4 mt-0.5 text-sky-500 shrink-0" />
        ) : (
        <button onClick={() => complete(it)} disabled={busy === it.id || !!it.requiresPostponement} data-testid={`agenda-complete-${it.id}`}
          className={`mt-0.5 text-slate-300 shrink-0 ${it.requiresPostponement ? "opacity-40 cursor-not-allowed" : "hover:text-emerald-500"}`}
          title={it.requiresPostponement ? (zh ? "请先完成顺延审批" : "Postponement approval required") : (zh ? "标记完成" : "Complete")}
          aria-label={it.requiresPostponement ? (zh ? "请先完成顺延审批" : "Postponement approval required") : (zh ? "标记完成" : "Complete")}>
          <Circle className="h-4 w-4" />
        </button>
        )}
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => it.projectAccessRestricted ? nav("/todos") : it.link && nav(it.link)}>
          <div className="flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${it.kind === "appt" ? "bg-sky-500" : prioDot(it.priority)}`} />
            <p className="font-medium text-slate-700 truncate">{visibleTitle(it)}</p>
            {it.overdue && <span className="text-[10px] text-red-500 font-bold inline-flex items-center gap-0.5 shrink-0"><AlertTriangle className="h-2.5 w-2.5" />{zh ? "逾期" : "overdue"}</span>}
            {!it.projectAccessRestricted && !it.overdue && it.kind === "crm" && <span className="text-[10px] text-amber-600 shrink-0">{zh ? "必做" : "must-do"}</span>}
            {!it.projectAccessRestricted && it.kind === "appt" && <span className="text-[10px] text-sky-600 font-semibold shrink-0" data-testid={`agenda-appt-kind-${it.id}`}>{it.apptKind === "rfq_visit" ? (zh ? "分包踏勘" : "site visit") : (zh ? "现场评估" : "site eval")}</span>}
            {!it.projectAccessRestricted && it.kind === "todo" && it.origin === "chat_auto" && <span className="text-[10px] text-violet-600 font-semibold shrink-0" data-testid={`agenda-chat-origin-${it.id}`} title={zh ? "由聊天承诺自动加入 · 点击查看原消息" : "Auto-added from chat · click to open the message"}>✦ {zh ? "聊天" : "chat"}</span>}
          </div>
          {!it.projectAccessRestricted && it.desc && <p className="text-[11px] text-slate-400 truncate mt-0.5">{it.desc}</p>}
          {/* 仍使用原待办页的申请/审批流程，避免工作台出现能绕开审批的第二套操作。 */}
          {!it.projectAccessRestricted && it.requiresPostponement && <a href="/todos?filter=overdue" onClick={(e) => { e.preventDefault(); e.stopPropagation(); nav("/todos?filter=overdue"); }} className="text-[11px] font-medium text-amber-700 hover:underline">{zh ? "申请或查看顺延" : "Request or view postponement"}</a>}
        </div>
        {!it.projectAccessRestricted && it.kind === "crm" && (
          <button onClick={(e) => { e.stopPropagation(); setDeferReason(""); setDeferItem(it); }} data-testid={`agenda-defer-${it.id}`} className="shrink-0 self-center text-[10px] font-medium text-amber-600 border border-amber-200 rounded px-1.5 py-0.5 hover:bg-amber-50">{zh ? "申请延期" : "Defer"}</button>
        )}
        {!it.projectAccessRestricted && it.kind === "todo" && it.origin === "proposal_read" && (
          <button onClick={(e) => { e.stopPropagation(); setOutcomeChoice(""); setOutcomeNote(""); setOutcomeItem(it); }} data-testid={`agenda-outcome-${it.id}`} className="shrink-0 self-center text-[10px] font-medium text-indigo-600 border border-indigo-200 rounded px-1.5 py-0.5 hover:bg-indigo-50">{zh ? "记录回访结果" : "Log outcome"}</button>
        )}
      </div>
    </div>
  );

  const firstThis = laterGroups.findIndex((g) => g.weekTag === "this");
  const firstNext = laterGroups.findIndex((g) => g.weekTag === "next");

  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="week-agenda">
      {celebrate && (
        <>
          <style>{`
            @keyframes agFall{0%{transform:translateY(-20vh) rotate(0);opacity:1}100%{transform:translateY(92vh) rotate(720deg);opacity:0}}
            @keyframes agPop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}
            .ag-confetti{position:absolute;top:-6vh;width:9px;height:14px;border-radius:2px;animation:agFall 2.7s linear forwards}
          `}</style>
          <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none" data-testid="agenda-celebration">
            {Array.from({ length: 28 }).map((_, i) => (
              <span key={i} className="ag-confetti" style={{ left: `${(i * 3.6) % 100}%`, animationDelay: `${(i % 9) * 0.12}s`, background: ["#ef4444", "#f59e0b", "#10b981", "#6366f1", "#ec4899", "#22d3ee"][i % 6] }} />
            ))}
            <div className="relative bg-white rounded-2xl shadow-2xl px-8 py-6 text-center" style={{ animation: "agPop .5s ease-out" }}>
              <div className="text-4xl mb-2">🎉</div>
              <p className="text-lg font-bold text-emerald-600">{zh ? "今日已清零！" : "All cleared!"}</p>
              <p className="text-sm text-slate-500 mt-1">{zh ? "可安心下班，辛苦啦 👏" : "Clock out anytime. Great job 👏"}</p>
            </div>
          </div>
        </>
      )}
      {/* 申请延期弹窗 */}
      <Dialog open={!!deferItem} onOpenChange={(o) => { if (!o) setDeferItem(null); }}>
        <DialogContent className="max-w-md" data-testid="agenda-defer-dialog">
          <DialogHeader>
            <DialogTitle>{zh ? "申请延期" : "Request Defer"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">{zh ? "提交给经理审批，通过后该任务顺延到明天。" : "Sent to your manager; if approved the task moves to tomorrow."}</p>
          {deferItem && <p className="text-sm font-medium text-slate-700">{deferItem.title}</p>}
          <Textarea value={deferReason} onChange={(e) => setDeferReason(e.target.value)} rows={3} data-testid="agenda-defer-reason" placeholder={zh ? "延期理由（必填）" : "Reason (required)"} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeferItem(null)}>{zh ? "取消" : "Cancel"}</Button>
            <Button onClick={submitDefer} disabled={deferBusy} data-testid="agenda-defer-submit" className="bg-amber-600 hover:bg-amber-700 text-white">{zh ? "提交申请" : "Submit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 记录回访结果弹窗 */}
      <Dialog open={!!outcomeItem} onOpenChange={(o) => { if (!o) setOutcomeItem(null); }}>
        <DialogContent className="max-w-md" data-testid="call-outcome-dialog">
          <DialogHeader>
            <DialogTitle>{zh ? "记录回访结果" : "Log Call Outcome"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">{zh ? "记录后任务自动完成，并同步到该客户的跟进时间线。" : "Completes the task and syncs to the lead timeline."}</p>
          {outcomeItem && <p className="text-sm font-medium text-slate-700">{outcomeItem.title}</p>}
          <div className="flex flex-wrap gap-1.5">
            {CALL_OUTCOMES.map((o) => (
              <button key={o.v} type="button" onClick={() => setOutcomeChoice(o.v)} data-testid={`call-outcome-opt-${o.v}`}
                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${outcomeChoice === o.v ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`}>{zh ? o.v : o.en}</button>
            ))}
          </div>
          <Textarea value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} rows={2} data-testid="call-outcome-note" placeholder={zh ? "备注（可选）" : "Note (optional)"} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOutcomeItem(null)}>{zh ? "取消" : "Cancel"}</Button>
            <Button onClick={submitOutcome} disabled={outcomeBusy} data-testid="call-outcome-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">{zh ? "记录并完成" : "Log & complete"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-brand-50 to-white">
        <CalendarDays className="h-4 w-4 text-brand-500 shrink-0" />
        <h3 className="text-sm font-bold text-slate-800">{zh ? "我的工作台 · 今日必做" : "My Agenda · Today First"}</h3>
        {pending > 0 ? (
          <span className="ml-1 text-[11px] font-bold text-red-600 bg-red-100 rounded-full px-2 py-0.5" data-testid="week-agenda-pending">{zh ? `今日待完成 ${pending} 项` : `${pending} due today`}</span>
        ) : (
          <span className="ml-1 text-[11px] font-bold text-emerald-600 bg-emerald-100 rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid="week-agenda-clear"><CheckCircle2 className="h-3 w-3" />{zh ? "今日已清零" : "All clear"}</span>
        )}
        {badges.length > 0 && (
          <span data-testid="agenda-badges" title={badges.map((b) => b.label).join("、")}
            className="hidden sm:inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 bg-rose-100 rounded-full px-2 py-0.5">
            🌸 {badges[0].milestone}{zh ? "天奖章" : "d"}{badges.length > 1 ? ` ×${badges.length}` : ""}
          </span>
        )}
        {user?.role !== "subcontractor" && (!clockedOut ? (
          <button onClick={clockOut} disabled={clocking || !canClockOut} data-testid="agenda-clock-out" className={`ml-auto text-[11px] font-semibold rounded-full px-3 py-1 inline-flex items-center gap-1 ${canClockOut ? "bg-brand-500 text-white hover:bg-brand-600" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
            <LogOut className="h-3 w-3" />{canClockOut ? (zh ? "打卡下班" : "Clock out") : (zh ? `还有 ${pending} 项` : `${pending} left`)}
          </button>
        ) : (
          <span className="ml-auto text-[11px] font-semibold text-emerald-600 inline-flex items-center gap-1" data-testid="agenda-clocked-out"><CheckCircle2 className="h-3 w-3" />{zh ? "已下班" : "Out"}</span>
        ))}
        {user?.role === "subcontractor" && <span className="ml-auto" />}
        <button onClick={printWeek} data-testid="week-agenda-print" title={zh ? "导出/打印清单" : "Print"} className="text-slate-400 hover:text-brand-500">
          <Printer className="h-4 w-4" />
        </button>
        <button onClick={() => setOpen(!open)} data-testid="week-agenda-toggle" className="text-slate-400 hover:text-slate-600">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div className="p-3">
          {/* Progress bar */}
          <div className="mb-3" data-testid="week-agenda-progress">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold text-slate-500">{zh ? "今日完成" : "Today"} <span className={allDone ? "text-emerald-600" : "text-slate-700"} data-testid="week-agenda-progress-count">{doneN}/{totalN}</span></span>
              {allDone
                ? <span className="text-[11px] font-bold text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />{zh ? "今日已清零 🎉" : "All done 🎉"}</span>
                : <span className="text-[11px] font-semibold text-slate-400" data-testid="week-agenda-progress-pct">{pct}%</span>}
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${pct}%` }} data-testid="week-agenda-progress-bar" />
            </div>
          </div>

          {/* Completed today (collapsible) */}
          {completedToday.length > 0 && (
            <div className="mb-3">
              <button onClick={() => setShowDone(!showDone)} data-testid="week-agenda-completed-toggle" className="text-[11px] text-emerald-600 font-semibold inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />{zh ? `今日已完成 (${completedToday.length})` : `Completed today (${completedToday.length})`}
                {showDone ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              {showDone && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="week-agenda-completed-list">
                  {completedToday.map((c) => (
                    <span key={c.id} className="text-[11px] text-slate-400 line-through bg-slate-50 border border-slate-100 rounded px-2 py-0.5">{visibleTitle(c)}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 今日必做 (today + overdue, red priority) */}
          <div className="rounded-xl border border-red-200 bg-red-50/40 p-2.5 mb-3" data-testid="agenda-today">
            <div className="flex items-center gap-1.5 mb-2">
              <Flame className="h-4 w-4 text-red-600" />
              <span className="text-xs font-bold text-red-700">{zh ? "今日必做（含逾期）" : "Today (incl. overdue)"}</span>
              <span className="text-[11px] text-red-500 font-semibold">{todayItems.length}</span>
            </div>
            {todayItems.length === 0 ? (
              <p className="text-[11px] text-emerald-600 text-center py-3 inline-flex items-center gap-1 w-full justify-center" data-testid="agenda-today-empty"><CheckCircle2 className="h-4 w-4" />{zh ? "今日已清零，安心下班 🎉" : "All done for today 🎉"}</p>
            ) : (
              <div className="space-y-1.5">{todayItems.map((it) => <ItemRow key={it.id} it={it} />)}</div>
            )}
          </div>

          {/* 接下来: 本周其余 + 下周 (scrollable) */}
          <div className={`space-y-2 ${embedded ? "max-h-[52vh]" : "max-h-[40vh]"} overflow-y-auto pr-1`} data-testid="agenda-later">
            {laterGroups.map((g, idx) => (
              <div key={g.date}>
                {idx === firstThis && firstThis >= 0 && <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-1 mb-1" data-testid="agenda-divider-this">{zh ? "本周其余" : "Rest of week"}</p>}
                {idx === firstNext && firstNext >= 0 && <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-400 mt-2 mb-1" data-testid="agenda-divider-next">{zh ? "下周" : "Next week"}</p>}
                <div className="rounded-xl border border-slate-200 p-2" data-testid={`agenda-day-${g.date}`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className={`text-[11px] font-bold ${g.weekTag === "next" ? "text-indigo-500" : "text-slate-600"}`}>{g.label}</span>
                    <span className="text-[10px] text-slate-300">{g.date.slice(5)}</span>
                    {g.items.length > 0 && <span className="ml-auto text-[10px] text-slate-400">{g.items.length}</span>}
                  </div>
                  {g.items.length === 0 ? (
                    <p className="text-[11px] text-slate-300 py-1 pl-1">{zh ? "无安排" : "—"}</p>
                  ) : (
                    <div className="space-y-1.5">{g.items.map((it) => <ItemRow key={it.id} it={it} dim />)}</div>
                  )}
                </div>
              </div>
            ))}
            {laterGroups.length === 0 && <p className="text-[11px] text-slate-300 text-center py-3">{zh ? "本周与下周暂无其它安排" : "Nothing else scheduled"}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
